import {adjustToTimeZone, ShiftEntity, startOfDay} from "@ots-frontend/ots-data";
import {getFormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";

export type DriverShiftContext = {
  status: string
  active: boolean
  shifts: ShiftEntity[]
  primaryShift?: ShiftEntity
  equipment?: string
  shiftWindow?: string
}

const getAppDayRange = (appDttm?: number): { startDttm: number, endDttm: number } | undefined => {
  if (typeof appDttm !== "number") {
    return undefined;
  }

  const selectedDate = new Date(appDttm);
  const startDttm = adjustToTimeZone(startOfDay(selectedDate)).getTime();
  return {
    startDttm,
    endDttm: startDttm + (24 * 60 * 60 * 1000),
  };
}

const doesShiftIntersectRange = (shift: ShiftEntity, range: { startDttm: number, endDttm: number } | undefined): boolean => {
  if (!range) {
    return true;
  }

  const shiftStartDttm = shift.startDttm;
  const shiftEndDttm = typeof shift.endDttm === "number" ? shift.endDttm : Number.POSITIVE_INFINITY;
  return shiftStartDttm < range.endDttm && shiftEndDttm >= range.startDttm;
}

export const isShiftActive = (shift: ShiftEntity): boolean => {
  return typeof shift.endDttm === "undefined";
}

export const getDriverShifts = (driverId: string, shifts: ShiftEntity[], appDttm?: number): ShiftEntity[] => {
  const appDayRange = getAppDayRange(appDttm);
  return shifts
    .filter((shift) => shift.personId === driverId)
    .filter((shift) => doesShiftIntersectRange(shift, appDayRange))
    .sort((a, b) => a.startDttm - b.startDttm);
}

export const getDriverShiftContext = (
  driverId: string | undefined,
  shifts: ShiftEntity[],
  appDttm?: number
): DriverShiftContext | undefined => {
  if (!driverId) {
    return undefined;
  }

  const driverShifts = getDriverShifts(driverId, shifts, appDttm);
  return getShiftContextFromShifts(driverShifts);
}

export const getShiftContextFromShifts = (driverShifts: ShiftEntity[]): DriverShiftContext => {
  const activeShift = driverShifts.find(isShiftActive);

  if (driverShifts.length === 0) {
    return {
      status: "Off Shift",
      active: false,
      shifts: driverShifts,
    };
  }

  const primaryShift = activeShift ?? driverShifts[0];
  const status = activeShift
    ? "In Shift"
    : `${driverShifts.length} Shift${driverShifts.length === 1 ? "" : "s"}`;
  const equipment = [primaryShift.vehicleName, primaryShift.trailerName].filter(Boolean).join(" / ");
  const shiftWindow = `${getFormattedDateTime(primaryShift.startDttm)} - ${typeof primaryShift.endDttm === "number" ? getFormattedDateTime(primaryShift.endDttm) : "Open"}`;

  return {
    status,
    active: Boolean(activeShift),
    shifts: driverShifts,
    primaryShift,
    equipment: equipment || undefined,
    shiftWindow,
  };
}
